import { create } from "zustand";
import { devtools } from "zustand/middleware";
import APIs from "../application/APIs";

// =============================================================================
// Types - Imported from generated types
// =============================================================================

import type {
  FlowStatusEnum,
  TransactionTypeEnum,
  TransactionStatusEnum,
  FlowConditionResponse,
  FlowEmailActionResponse,
  FlowTemplateActionResponse,
  GeneratedDocumentResponse,
  MatchedFlowEmailAction as GenMatchedFlowEmailAction,
  MatchedFlowTemplateAction as GenMatchedFlowTemplateAction,
  MatchedFlow as GenMatchedFlow,
  FlowResponse,
  FlowRunResponse,
  TransactionResponse,
  ClientOption,
  CustodianOption,
  InstrumentOption,
  FlowFieldOption as GenFlowFieldOption,
  FlowFieldDefinition as GenFlowFieldDefinition,
  EmailVariableInfo as GenEmailVariableInfo,
} from "../types/generatedTypes";

// Type aliases - re-export generated types under names used by components
export type FlowStatus = FlowStatusEnum;
export type TransactionType = TransactionTypeEnum;
export type TransactionStatus = TransactionStatusEnum;
export type FlowCondition = FlowConditionResponse;
export type FlowEmailAction = FlowEmailActionResponse;
export type FlowTemplateAction = FlowTemplateActionResponse;
export type GeneratedDocumentForFlow = GeneratedDocumentResponse;
export type MatchedFlowEmailAction = GenMatchedFlowEmailAction;
export type MatchedFlowTemplateAction = GenMatchedFlowTemplateAction;
export type MatchedFlow = GenMatchedFlow;
export type Flow = FlowResponse;
export type FlowRun = FlowRunResponse;
export type Transaction = TransactionResponse;
export type FlowClient = ClientOption;
export type FlowCustodian = CustodianOption;
export type FlowInstrument = InstrumentOption;
export type FlowFieldOption = GenFlowFieldOption;
export type FlowFieldDefinition = GenFlowFieldDefinition;
export type EmailVariableInfo = GenEmailVariableInfo;

// Flows Tab view states (frontend-only, no generated equivalent)
export type FlowsSubView = "menu" | "transactions" | "automation-flow" | "flows-manager";

// =============================================================================
// Store Interface
// =============================================================================

interface FlowsState {
  // Current sub-view in Flows tab
  currentSubView: FlowsSubView;

  // Flows State
  flows: Flow[];
  flowsLoading: boolean;
  selectedFlow: Flow | null;
  editingFlow: Partial<Flow> | null;

  // Draft Flows Banner State
  draftFlowsCount: number;
  isDraftBannerDismissed: boolean;

  // Flow Runs State
  flowRuns: FlowRun[];
  flowRunsLoading: boolean;

  // Transactions State
  transactions: Transaction[];
  transactionsLoading: boolean;
  pendingTransactions: Transaction[];

  // Data Sources State (for dropdowns)
  clients: FlowClient[];
  custodians: FlowCustodian[];
  instruments: FlowInstrument[];
  flowFields: FlowFieldDefinition[];
  operators: FlowFieldOption[];
  emailVariables: EmailVariableInfo[];
  generatedDocumentsForFlows: GeneratedDocumentForFlow[];
  dataLoading: boolean;

  // Flow Matching State
  matchedFlowsForBundle: MatchedFlow[];
  matchingFlowsLoading: boolean;

  // Error State
  error: string | null;

  // Navigation Actions
  setSubView: (view: FlowsSubView) => void;

  // Draft Banner Actions
  dismissDraftBanner: () => void;
  resumeDrafts: () => void;

  // Flow Actions
  fetchFlows: (status?: string) => Promise<void>;
  fetchFlow: (flowId: string) => Promise<void>;
  createFlow: (data: Partial<Flow>) => Promise<string | null>;
  updateFlow: (flowId: string, data: Partial<Flow>) => Promise<void>;
  deleteFlow: (flowId: string) => Promise<void>;
  duplicateFlow: (flowId: string, newName?: string) => Promise<string | null>;
  executeFlow: (flowId: string, triggerData?: Record<string, unknown>) => Promise<boolean>;
  testFlow: (flowId: string, triggerData?: Record<string, unknown>) => Promise<{
    conditions_met: boolean;
    simulated_actions: Array<{ action: string; would_execute: boolean }>;
  } | null>;
  matchFlowsForTransaction: (transactionData: Record<string, unknown>) => Promise<MatchedFlow[]>;
  clearMatchedFlows: () => void;
  setSelectedFlow: (flow: Flow | null) => void;
  setEditingFlow: (flow: Partial<Flow> | null) => void;

  // Flow Runs Actions
  fetchFlowRuns: (flowId?: string, status?: string) => Promise<void>;
  recordFlowRun: (data: {
    flow_id: string;
    flow_name: string;
    client_id?: string;
    client_name?: string;
    trigger_data?: Record<string, unknown>;
    status?: "completed" | "failed";
  }) => Promise<void>;

  // Transaction Actions
  fetchTransactions: (params?: { status?: string; transaction_type?: string; client_id?: string }) => Promise<void>;
  createTransaction: (data: Partial<Transaction>) => Promise<boolean>;
  createBulkTransactions: (transactions: Array<Partial<Transaction>>) => Promise<{ created: number; errors: string[] }>;
  updateTransaction: (transactionId: string, data: Partial<Transaction>) => Promise<void>;
  deleteTransaction: (transactionId: string) => Promise<void>;
  validateTransactions: () => Promise<{ validated: number; failed: number }>;
  submitTransactions: () => Promise<number>;
  sendTransactionEmail: (data: {
    to: string;
    cc?: string;
    subject: string;
    body: string;
    transactionIds: string[];
    documentIds?: string[];
    localFiles?: File[];
  }) => Promise<boolean>;

  // Data Source Actions
  fetchDataSources: () => Promise<void>;
  fetchClients: () => Promise<void>;
  fetchCustodians: () => Promise<void>;
  fetchInstruments: () => Promise<void>;
  fetchFlowFields: () => Promise<void>;
  fetchEmailVariables: () => Promise<void>;
  fetchGeneratedDocumentsForFlows: () => Promise<void>;

  // Utility
  clearError: () => void;
}

// =============================================================================
// Store Implementation
// =============================================================================

const useFlowsStore = create<FlowsState>()(
  devtools(
    (set, get) => ({
      // Initial State
      currentSubView: "menu",
      flows: [],
      flowsLoading: false,
      selectedFlow: null,
      editingFlow: null,
      draftFlowsCount: 0,
      isDraftBannerDismissed: false,
      flowRuns: [],
      flowRunsLoading: false,
      transactions: [],
      transactionsLoading: false,
      pendingTransactions: [],
      clients: [],
      custodians: [],
      instruments: [],
      flowFields: [],
      operators: [],
      emailVariables: [],
      generatedDocumentsForFlows: [],
      dataLoading: false,
      matchedFlowsForBundle: [],
      matchingFlowsLoading: false,
      error: null,

      // Navigation Actions
      setSubView: (view) => set({ currentSubView: view }),

      // Draft Banner Actions
      dismissDraftBanner: () => set({ isDraftBannerDismissed: true }),
      resumeDrafts: () => {
        set({ currentSubView: "flows-manager" });
      },

      // Flow Actions
      fetchFlows: async (status?: string) => {
        set({ flowsLoading: true, error: null });
        try {
          const response = await APIs.getFlows(status);
          const flows = response.flows as Flow[];
          const draftFlowsCount = flows.filter((f) => f.status === "draft").length;
          set({ flows, draftFlowsCount, flowsLoading: false });
        } catch (error) {
          console.error("Failed to fetch flows:", error);
          set({ error: "Failed to fetch flows", flowsLoading: false });
        }
      },

      fetchFlow: async (flowId: string) => {
        set({ flowsLoading: true, error: null });
        try {
          const flow = await APIs.getFlow(flowId);
          set({ selectedFlow: flow as Flow, flowsLoading: false });
        } catch (error) {
          console.error("Failed to fetch flow:", error);
          set({ error: "Failed to fetch flow", flowsLoading: false });
        }
      },

      createFlow: async (data) => {
        set({ error: null });
        try {
          const response = await APIs.createFlow(data as {
            name: string;
            description?: string;
            status?: string;
            conditions?: FlowCondition[];
            conditions_logic?: string;
            email_action?: FlowEmailAction;
            template_action?: FlowTemplateAction;
          });
          const flowId = response.data?.flow?.identifier;
          if (flowId) {
            await get().fetchFlows();
            return flowId;
          }
          return null;
        } catch (error) {
          console.error("Failed to create flow:", error);
          set({ error: "Failed to create flow" });
          return null;
        }
      },

      updateFlow: async (flowId, data) => {
        set({ error: null });
        try {
          await APIs.updateFlow(flowId, data);
          await get().fetchFlows();
        } catch (error) {
          console.error("Failed to update flow:", error);
          set({ error: "Failed to update flow" });
        }
      },

      deleteFlow: async (flowId) => {
        try {
          await APIs.deleteFlow(flowId);
          set((state) => ({
            flows: state.flows.filter((f) => f.identifier !== flowId),
            selectedFlow: state.selectedFlow?.identifier === flowId ? null : state.selectedFlow,
          }));
        } catch (error) {
          console.error("Failed to delete flow:", error);
          set({ error: "Failed to delete flow" });
        }
      },

      duplicateFlow: async (flowId, newName) => {
        set({ error: null });
        try {
          const response = await APIs.duplicateFlow(flowId, newName);
          const duplicatedFlowId = response.data?.flow?.identifier;
          if (duplicatedFlowId) {
            await get().fetchFlows();
            return duplicatedFlowId;
          }
          return null;
        } catch (error) {
          console.error("Failed to duplicate flow:", error);
          set({ error: "Failed to duplicate flow" });
          return null;
        }
      },

      executeFlow: async (flowId, triggerData = {}) => {
        set({ error: null });
        try {
          const response = await APIs.executeFlow(flowId, { trigger_data: triggerData });
          if (response.data?.action_taken) {
            return true;
          }
          return false;
        } catch (error) {
          console.error("Failed to execute flow:", error);
          set({ error: "Failed to execute flow" });
          return false;
        }
      },

      testFlow: async (flowId, triggerData = {}) => {
        set({ error: null });
        try {
          const response = await APIs.testFlow(flowId, { trigger_data: triggerData });
          if (response.data) {
            return {
              conditions_met: response.data.conditions_met || false,
              simulated_actions: response.data.simulated_actions || [],
            };
          }
          return null;
        } catch (error) {
          console.error("Failed to test flow:", error);
          set({ error: "Failed to test flow" });
          return null;
        }
      },

      matchFlowsForTransaction: async (transactionData) => {
        set({ matchingFlowsLoading: true, error: null });
        try {
          const response = await APIs.matchFlowsForTransaction(transactionData);
          const matchedFlows = response.matching_flows as MatchedFlow[];
          set({ matchedFlowsForBundle: matchedFlows, matchingFlowsLoading: false });
          return matchedFlows;
        } catch (error) {
          console.error("Failed to match flows:", error);
          set({ error: "Failed to match flows", matchingFlowsLoading: false });
          return [];
        }
      },

      clearMatchedFlows: () => set({ matchedFlowsForBundle: [] }),

      setSelectedFlow: (flow) => set({ selectedFlow: flow }),
      setEditingFlow: (flow) => set({ editingFlow: flow }),

      // Flow Runs Actions
      fetchFlowRuns: async (flowId?: string, status?: string) => {
        set({ flowRunsLoading: true, error: null });
        try {
          const response = await APIs.getFlowRuns({ flow_id: flowId, status });
          set({ flowRuns: response.runs as FlowRun[], flowRunsLoading: false });
        } catch (error) {
          console.error("Failed to fetch flow runs:", error);
          set({ error: "Failed to fetch flow runs", flowRunsLoading: false });
        }
      },

      recordFlowRun: async (data) => {
        try {
          await APIs.recordFlowRun({
            flow_id: data.flow_id,
            flow_name: data.flow_name,
            client_id: data.client_id,
            client_name: data.client_name,
            trigger_data: data.trigger_data,
            status: data.status || "completed",
          });
          // Refresh flow runs to update dashboard
          get().fetchFlowRuns();
        } catch (error) {
          console.error("Failed to record flow run:", error);
        }
      },

      // Transaction Actions
      fetchTransactions: async (params) => {
        set({ transactionsLoading: true, error: null });
        try {
          const response = await APIs.getTransactions(params);
          const transactions = response.transactions as Transaction[];
          set({
            transactions,
            pendingTransactions: transactions.filter((t) => t.status === "pending"),
            transactionsLoading: false,
          });
        } catch (error) {
          console.error("Failed to fetch transactions:", error);
          set({ error: "Failed to fetch transactions", transactionsLoading: false });
        }
      },

      createTransaction: async (data) => {
        set({ error: null });
        try {
          await APIs.createTransaction(data as {
            transaction_type: string;
            client_id: string;
            client_name: string;
            amount: number;
            currency: string;
          });
          await get().fetchTransactions();
          return true;
        } catch (error) {
          console.error("Failed to create transaction:", error);
          set({ error: "Failed to create transaction" });
          return false;
        }
      },

      createBulkTransactions: async (transactions) => {
        set({ error: null });
        try {
          const response = await APIs.createBulkTransactions(
            transactions as Array<{
              transaction_type: string;
              client_id: string;
              client_name: string;
              amount: number;
              currency: string;
            }>
          );
          await get().fetchTransactions();
          return {
            created: response.data?.created_count || 0,
            errors: response.data?.errors || [],
          };
        } catch (error) {
          console.error("Failed to create bulk transactions:", error);
          set({ error: "Failed to create bulk transactions" });
          return { created: 0, errors: ["Failed to create transactions"] };
        }
      },

      updateTransaction: async (transactionId, data) => {
        set({ error: null });
        try {
          await APIs.updateTransaction(transactionId, data);
          await get().fetchTransactions();
        } catch (error) {
          console.error("Failed to update transaction:", error);
          set({ error: "Failed to update transaction" });
        }
      },

      deleteTransaction: async (transactionId) => {
        try {
          await APIs.deleteTransaction(transactionId);
          set((state) => ({
            transactions: state.transactions.filter((t) => t.identifier !== transactionId),
            pendingTransactions: state.pendingTransactions.filter((t) => t.identifier !== transactionId),
          }));
        } catch (error) {
          console.error("Failed to delete transaction:", error);
          set({ error: "Failed to delete transaction" });
        }
      },

      validateTransactions: async () => {
        set({ error: null });
        try {
          const response = await APIs.validateTransactions();
          await get().fetchTransactions();
          return {
            validated: response.data?.validated_count || 0,
            failed: response.data?.failed_count || 0,
          };
        } catch (error) {
          console.error("Failed to validate transactions:", error);
          set({ error: "Failed to validate transactions" });
          return { validated: 0, failed: 0 };
        }
      },

      submitTransactions: async () => {
        set({ error: null });
        try {
          const response = await APIs.submitTransactions();
          await get().fetchTransactions();
          return response.data?.submitted_count || 0;
        } catch (error) {
          console.error("Failed to submit transactions:", error);
          set({ error: "Failed to submit transactions" });
          return 0;
        }
      },

      sendTransactionEmail: async (data) => {
        set({ error: null });
        try {
          const response = await APIs.sendTransactionEmail(
            {
              to: data.to,
              cc: data.cc,
              subject: data.subject,
              body: data.body,
              transaction_ids: data.transactionIds,
              document_ids: data.documentIds,
            },
            data.localFiles
          );
          if (response.data?.email_sent) {
            await get().fetchTransactions();
            return true;
          }
          return false;
        } catch (error) {
          console.error("Failed to send transaction email:", error);
          set({ error: "Failed to send email" });
          return false;
        }
      },

      // Data Source Actions
      fetchDataSources: async () => {
        set({ dataLoading: true, error: null });
        try {
          await Promise.all([
            get().fetchClients(),
            get().fetchCustodians(),
            get().fetchInstruments(),
            get().fetchFlowFields(),
            get().fetchEmailVariables(),
          ]);
          set({ dataLoading: false });
        } catch (error) {
          console.error("Failed to fetch data sources:", error);
          set({ error: "Failed to fetch data sources", dataLoading: false });
        }
      },

      fetchClients: async () => {
        try {
          const response = await APIs.getFlowClients();
          set({ clients: response.clients });
        } catch (error) {
          console.error("Failed to fetch clients:", error);
        }
      },

      fetchCustodians: async () => {
        try {
          const response = await APIs.getFlowCustodians();
          set({ custodians: response.custodians });
        } catch (error) {
          console.error("Failed to fetch custodians:", error);
        }
      },

      fetchInstruments: async () => {
        try {
          const response = await APIs.getFlowInstruments();
          set({ instruments: response.instruments });
        } catch (error) {
          console.error("Failed to fetch instruments:", error);
        }
      },

      fetchFlowFields: async () => {
        try {
          const response = await APIs.getFlowFields();
          set({
            flowFields: response.fields,
            operators: response.operators,
          });
        } catch (error) {
          console.error("Failed to fetch flow fields:", error);
        }
      },

      fetchEmailVariables: async () => {
        try {
          const response = await APIs.getEmailVariables();
          set({ emailVariables: response.variables });
        } catch (error) {
          console.error("Failed to fetch email variables:", error);
        }
      },

      fetchGeneratedDocumentsForFlows: async () => {
        try {
          const response = await APIs.getGeneratedDocumentsForFlows();
          set({ generatedDocumentsForFlows: response.documents });
        } catch (error) {
          console.error("Failed to fetch generated documents for flows:", error);
        }
      },

      // Utility
      clearError: () => set({ error: null }),
    }),
    { name: "flows-store" }
  )
);

// Custom hook for using the store
export const useFlows = () => useFlowsStore();

export default useFlowsStore;
